import React, { useState, useEffect } from 'react';
import { 
  Receipt, CheckCircle2, XCircle, Clock, Eye, ArrowDown,
  Plane, Heart, Monitor, GraduationCap, Utensils, FileText,
  Search, DollarSign, AlertCircle, User, Building2
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../ui/card';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Badge } from '../../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../ui/table';
import { Textarea } from '../../ui/textarea';
import { Label } from '../../ui/label';
import { db } from '../../../lib/db';
import { formatCurrency, getPayrollStatusColor, formatPayrollStatus, getReimbursementCategoryInfo } from '../../../lib/payrollCalculations';
import { formatDate, formatTimeAgo } from '../../../lib/dateUtils';
import { useAuthStore } from '../../../store/authStore';
import { toast } from 'sonner';

const categoryIcons = {
  travel: Plane,
  medical: Heart,
  equipment: Monitor,
  training: GraduationCap,
  meals: Utensils,
  other: FileText
};

export default function ReimbursementReview() {
  const { user } = useAuthStore();
  const [loading, setLoading] = useState(true);
  const [reimbursements, setReimbursements] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [departmentFilter, setDepartmentFilter] = useState('all');
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedReimbursement, setSelectedReimbursement] = useState(null);
  const [rejectionReason, setRejectionReason] = useState('');
  const [ceoComment, setCeoComment] = useState('');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [usersData, reimbursementsData] = await Promise.all([
        db.users.toArray(),
        db.reimbursements.toArray()
      ]);
      
      setAllUsers(usersData);
      // Get all reimbursements forwarded to CEO, high-value ones, or from supervisors
      const ceoReimbursements = reimbursementsData.filter(r => {
        const requestor = usersData.find(u => u.id === r.userId);
        // Show if: forwarded to CEO, high-value, or from supervisor (pending status)
        return r.status === 'pending_ceo' || 
          (r.status === 'pending' && r.amount >= 1000) ||
          (r.status === 'pending' && requestor?.role === 'supervisor');
      });
      setReimbursements(ceoReimbursements.sort((a, b) => new Date(b.submittedAt) - new Date(a.submittedAt)));
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const departments = [...new Set(allUsers.map(u => u.profile?.department).filter(Boolean))];

  const filteredReimbursements = reimbursements.filter(r => {
    const employee = allUsers.find(u => u.id === r.userId);
    const matchesSearch = 
      employee?.profile?.firstName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      employee?.profile?.lastName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      r.description?.toLowerCase().includes(searchQuery.toLowerCase());
    
    // Handle different status filter options
    let matchesStatus = false;
    if (statusFilter === 'all') {
      matchesStatus = true;
    } else if (statusFilter === 'pending') {
      // Show all pending (pending + pending_ceo)
      matchesStatus = r.status === 'pending' || r.status === 'pending_ceo';
    } else if (statusFilter === 'supervisor_pending') {
      // Show only supervisor's pending requests
      matchesStatus = r.status === 'pending' && employee?.role === 'supervisor';
    } else {
      matchesStatus = r.status === statusFilter;
    }
    
    const matchesDepartment = departmentFilter === 'all' || 
      employee?.profile?.department === departmentFilter;
    return matchesSearch && matchesStatus && matchesDepartment;
  });

  const pendingCeoCount = reimbursements.filter(r => r.status === 'pending_ceo').length;
  const pendingCeoAmount = reimbursements.filter(r => r.status === 'pending_ceo')
    .reduce((sum, r) => sum + (Number(r.amount) || 0), 0);
  const highValueCount = reimbursements.filter(r => r.status === 'pending' && r.amount >= 1000).length;
  const supervisorPendingCount = reimbursements.filter(r => {
    const requestor = allUsers.find(u => u.id === r.userId);
    return r.status === 'pending' && requestor?.role === 'supervisor';
  }).length;
  const totalPendingAmount = reimbursements.filter(r => r.status === 'pending' || r.status === 'pending_ceo')
    .reduce((sum, r) => sum + (Number(r.amount) || 0), 0);

  const getEmployeeName = (userId) => {
    const member = allUsers.find(u => u.id === userId);
    return member ? `${member.profile?.firstName} ${member.profile?.lastName}` : 'Unknown';
  };

  const getEmployeeDetails = (userId) => {
    return allUsers.find(u => u.id === userId);
  };

  const getForwarderName = (forwardedBy) => {
    const forwarder = allUsers.find(u => u.id === forwardedBy);
    return forwarder ? `${forwarder.profile?.firstName} ${forwarder.profile?.lastName}` : 'Unknown';
  };

  const handleApprove = async (id) => {
    try {
      await db.reimbursements.update(id, {
        status: 'approved',
        approvedBy: user.id,
        approvedAt: new Date().toISOString(),
        ceoComment: ceoComment || 'Approved by CEO'
      });
      toast.success('Reimbursement approved');
      setShowDetailModal(false);
      setCeoComment('');
      loadData();
    } catch (error) {
      toast.error('Failed to approve reimbursement');
    }
  };

  const handleReject = async (id) => {
    if (!rejectionReason) {
      toast.error('Please provide a rejection reason');
      return;
    }
    try {
      await db.reimbursements.update(id, {
        status: 'rejected',
        rejectionReason,
        rejectedBy: user.id,
        rejectedAt: new Date().toISOString(),
        ceoComment
      });
      toast.success('Reimbursement rejected');
      setShowDetailModal(false);
      setRejectionReason('');
      setCeoComment('');
      loadData();
    } catch (error) {
      toast.error('Failed to reject reimbursement');
    }
  };

  const handleSendBack = async (id) => {
    try {
      await db.reimbursements.update(id, {
        status: 'pending',
        sentBackBy: user.id,
        sentBackAt: new Date().toISOString(),
        ceoComment: ceoComment || 'Sent back to supervisor for review'
      });
      toast.success('Reimbursement sent back to supervisor');
      setShowDetailModal(false);
      setCeoComment('');
      loadData();
    } catch (error) {
      toast.error('Failed to send back reimbursement');
    }
  };

  const viewDetails = (reimbursement) => {
    setSelectedReimbursement(reimbursement);
    setRejectionReason('');
    setCeoComment('');
    setShowDetailModal(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Reimbursement Review</h1>
          <p className="text-gray-500 mt-1">Review escalated, supervisor, and high-value expense claims</p>
        </div>
        {(pendingCeoCount + supervisorPendingCount) > 0 && (
          <Badge className="bg-amber-100 text-amber-700 px-3 py-1 text-sm">
            <AlertCircle className="w-4 h-4 mr-2" />
            {pendingCeoCount + supervisorPendingCount} Awaiting Your Review
          </Badge>
        )}
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
        <Card className="border-0 shadow-sm bg-gradient-to-r from-indigo-500 to-purple-600 text-white">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-indigo-100">Total Pending</p>
                <p className="text-3xl font-bold mt-1">{pendingCeoCount + supervisorPendingCount + highValueCount}</p>
              </div>
              <Receipt className="w-8 h-8 text-white/80" />
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Supervisor Requests</p>
                <p className="text-2xl font-bold text-teal-600">{supervisorPendingCount}</p>
              </div>
              <User className="w-8 h-8 text-teal-600" />
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Pending Amount</p>
                <p className="text-2xl font-bold text-amber-600">{formatCurrency(totalPendingAmount)}</p>
              </div>
              <DollarSign className="w-8 h-8 text-amber-600" />
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Escalated by Supervisors</p>
                <p className="text-2xl font-bold text-purple-600">{pendingCeoCount}</p>
              </div>
              <ArrowDown className="w-8 h-8 text-purple-600" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card className="border-0 shadow-sm">
        <CardContent className="py-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <Input
                placeholder="Search by employee or description..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={departmentFilter} onValueChange={setDepartmentFilter}>
              <SelectTrigger className="w-full sm:w-48">
                <SelectValue placeholder="Department" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Departments</SelectItem>
                {departments.map(dept => (
                  <SelectItem key={dept} value={dept}>{dept}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full sm:w-48">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="pending">All Pending</SelectItem>
                <SelectItem value="pending_ceo">Forwarded by Supervisor</SelectItem>
                <SelectItem value="supervisor_pending">Supervisor Requests</SelectItem>
                <SelectItem value="approved">Approved</SelectItem>
                <SelectItem value="rejected">Rejected</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Reimbursements List */}
      <Card className="border-0 shadow-sm">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="bg-gray-50">
                <TableHead>Employee</TableHead>
                <TableHead>Department</TableHead>
                <TableHead>Category</TableHead>
                <TableHead>Description</TableHead>
                <TableHead className="text-right">Amount</TableHead>
                <TableHead>Forwarded By</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-center">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredReimbursements.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-8 text-gray-500">
                    No reimbursements requiring CEO review
                  </TableCell>
                </TableRow>
              ) : (
                filteredReimbursements.map(reimbursement => {
                  const categoryInfo = getReimbursementCategoryInfo(reimbursement.category);
                  const Icon = categoryIcons[reimbursement.category] || FileText;
                  const employee = getEmployeeDetails(reimbursement.userId);
                  
                  return (
                    <TableRow 
                      key={reimbursement.id} 
                      className={`hover:bg-gray-50 ${reimbursement.status === 'pending_ceo' ? 'bg-purple-50/30' : ''}`}
                    >
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 bg-gradient-to-br from-violet-400 to-purple-500 rounded-full flex items-center justify-center text-white text-xs font-medium">
                            {getEmployeeName(reimbursement.userId).split(' ').map(n => n[0]).join('')}
                          </div>
                          <div>
                            <span className="font-medium">{getEmployeeName(reimbursement.userId)}</span>
                            <p className="text-xs text-gray-500">{employee?.profile?.position}</p>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="text-gray-600">
                        {employee?.profile?.department || '-'}
                      </TableCell>
                      <TableCell>
                        <Badge className={categoryInfo.color}>
                          <Icon className="w-3 h-3 mr-1" />
                          {categoryInfo.label}
                        </Badge>
                      </TableCell>
                      <TableCell className="max-w-xs truncate text-gray-600">
                        {reimbursement.description || '-'}
                      </TableCell>
                      <TableCell className="text-right">
                        <span className={`font-semibold ${reimbursement.amount >= 1000 ? 'text-purple-600' : ''}`}>
                          {formatCurrency(reimbursement.amount)}
                        </span>
                        {reimbursement.amount >= 1000 && (
                          <Badge className="ml-2 bg-purple-100 text-purple-700 text-xs">High</Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-gray-500 text-sm">
                        {reimbursement.forwardedBy ? getForwarderName(reimbursement.forwardedBy) : '-'}
                      </TableCell>
                      <TableCell>
                        <Badge className={getPayrollStatusColor(reimbursement.status)}>
                          {reimbursement.status === 'pending_ceo' ? 'Pending CEO' : formatPayrollStatus(reimbursement.status)}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-center">
                        <div className="flex justify-center gap-1">
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => viewDetails(reimbursement)}
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                          {(reimbursement.status === 'pending_ceo' || reimbursement.status === 'pending') && (
                            <>
                              <Button 
                                variant="ghost" 
                                size="sm" 
                                className="text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50"
                                onClick={() => handleApprove(reimbursement.id)}
                              >
                                <CheckCircle2 className="w-4 h-4" />
                              </Button>
                              <Button 
                                variant="ghost" 
                                size="sm" 
                                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                onClick={() => viewDetails(reimbursement)}
                              >
                                <XCircle className="w-4 h-4" />
                              </Button>
                            </>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Detail Modal */}
      <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
        <DialogContent className="sm:max-w-lg" style={{height: '95vh', maxHeight: '95vh', overflowY: 'auto'}}>
          <DialogHeader>
            <DialogTitle>Reimbursement Details</DialogTitle>
            <DialogDescription>
              Review the escalated expense claim
            </DialogDescription>
          </DialogHeader>

          {selectedReimbursement && (
            <div className="space-y-4">
              {/* Employee Info */}
              <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg">
                <div className="w-12 h-12 bg-gradient-to-br from-violet-400 to-purple-500 rounded-full flex items-center justify-center text-white font-medium">
                  {getEmployeeName(selectedReimbursement.userId).split(' ').map(n => n[0]).join('')}
                </div>
                <div className="flex-1">
                  <p className="font-semibold text-gray-900">{getEmployeeName(selectedReimbursement.userId)}</p>
                  <p className="text-sm text-gray-500">
                    {getEmployeeDetails(selectedReimbursement.userId)?.profile?.position} • 
                    {getEmployeeDetails(selectedReimbursement.userId)?.profile?.department}
                  </p>
                </div>
              </div>

              {/* Forwarding Info */}
              {selectedReimbursement.forwardedBy && (
                <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                  <p className="text-sm font-medium text-blue-700 flex items-center gap-2">
                    <User className="w-4 h-4" />
                    Forwarded by {getForwarderName(selectedReimbursement.forwardedBy)}
                  </p>
                  <p className="text-sm text-blue-600 mt-1">
                    {selectedReimbursement.forwardNote || 'For CEO review and approval'}
                  </p>
                  <p className="text-xs text-blue-500 mt-1">
                    {formatDate(selectedReimbursement.forwardedAt)}
                  </p>
                </div>
              )}

              {/* Claim Details */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-500">Category</p>
                  <Badge className={getReimbursementCategoryInfo(selectedReimbursement.category).color}>
                    {getReimbursementCategoryInfo(selectedReimbursement.category).label}
                  </Badge>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Amount</p>
                  <p className="text-xl font-bold text-gray-900">
                    {formatCurrency(selectedReimbursement.amount)}
                    {selectedReimbursement.amount >= 1000 && (
                      <Badge className="ml-2 bg-purple-100 text-purple-700 text-xs">High Value</Badge>
                    )}
                  </p>
                </div>
              </div>

              <div>
                <p className="text-sm text-gray-500 mb-1">Description</p>
                <p className="text-gray-900">{selectedReimbursement.description || 'No description provided'}</p>
              </div>

              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-gray-500">Submitted</p>
                  <p className="font-medium">{formatDate(selectedReimbursement.submittedAt)}</p>
                </div>
                <div>
                  <p className="text-gray-500">Status</p>
                  <Badge className={getPayrollStatusColor(selectedReimbursement.status)}>
                    {selectedReimbursement.status === 'pending_ceo' ? 'Pending CEO' : formatPayrollStatus(selectedReimbursement.status)}
                  </Badge>
                </div>
              </div>

              {(selectedReimbursement.status === 'pending_ceo' || selectedReimbursement.status === 'pending') && (
                <>
                  <div>
                    <Label htmlFor="ceoComment">Your Comments (optional)</Label>
                    <Textarea
                      id="ceoComment"
                      value={ceoComment}
                      onChange={(e) => setCeoComment(e.target.value)}
                      placeholder="Add any comments or notes..."
                      className="mt-1"
                      rows={2}
                    />
                  </div>
                  <div>
                    <Label htmlFor="rejectionReason">Rejection Reason (if rejecting)</Label>
                    <Textarea
                      id="rejectionReason"
                      value={rejectionReason}
                      onChange={(e) => setRejectionReason(e.target.value)}
                      placeholder="Enter reason for rejection..."
                      className="mt-1"
                      rows={2}
                    />
                  </div>
                </>
              )}
            </div>
          )}

          <DialogFooter className="flex-wrap gap-2">
            <Button variant="outline" onClick={() => setShowDetailModal(false)}>Close</Button>
            {(selectedReimbursement?.status === 'pending_ceo' || selectedReimbursement?.status === 'pending') && (
              <>
                {selectedReimbursement?.status === 'pending_ceo' && (
                  <Button 
                    variant="outline"
                    className="text-amber-600 border-amber-200 hover:bg-amber-50"
                    onClick={() => handleSendBack(selectedReimbursement.id)}
                  >
                    <ArrowDown className="w-4 h-4 mr-2" /> Send Back
                  </Button>
                )}
                <Button 
                  variant="outline"
                  className="text-red-600 border-red-200 hover:bg-red-50"
                  onClick={() => handleReject(selectedReimbursement.id)}
                >
                  <XCircle className="w-4 h-4 mr-2" /> Reject
                </Button>
                <Button 
                  onClick={() => handleApprove(selectedReimbursement.id)}
                  className="bg-gradient-to-r from-teal-500 to-emerald-600 hover:from-teal-600 hover:to-emerald-700 text-white"
                >
                  <CheckCircle2 className="w-4 h-4 mr-2" /> Approve
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
